import { Box, Button, Heading, Stack, Text } from '@chakra-ui/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ResellerContract,
  ResellerCustomer,
  ResellerReportResponse,
} from 'services/interfaces/reseller';
import { getResellerReport, listResellerCustomers } from 'services/reseller';
import { onlyDocumentChars } from 'shared/helpers/string';
import { usePermissions } from 'shared/hooks/usePermissions';
import ResellerHome from './ResellerHome';

interface ResellerContextData {
  customers: ResellerCustomer[];
  loading: boolean;
  total: number;
  page: number;
  setPage(n: number): void;
  pageSize: number;
  setPageSize(n: number): void;
  searchText: string;
  setSearchText(s: string): void;
  createModalOpen: boolean;
  setCreateModalOpen(b: boolean): void;
  reload(): void;
  reportData: ResellerReportResponse | null;
  loadingReport: boolean;
}

const Context = createContext<ResellerContextData>({} as ResellerContextData);

export const useResellerContext = () => useContext(Context);

const ResellerContext = () => {
  const { e } = usePermissions();
  const canRead = e('reseller-read');

  const [contracts, setContracts] = useState<ResellerContract[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchTextRaw] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [reportData, setReportData] = useState<ResellerReportResponse | null>(
    null,
  );
  const [loadingReport, setLoadingReport] = useState(false);

  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();
  const requestId = useRef(0);

  const setSearchText = useCallback((text: string) => {
    setSearchTextRaw(text);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setPage(1);
      setDebouncedQuery(text);
    }, 400);
  }, []);

  useEffect(() => {
    return () => clearTimeout(debounceTimer.current);
  }, []);

  const fetchCustomers = useCallback(() => {
    const id = ++requestId.current;
    setLoading(true);

    const offset = (page - 1) * pageSize;
    const query = debouncedQuery || undefined;
    const isDocumentSearch = query ? /^\d+([./-]\d+)*$/.test(query) : false;

    const params = {
      limit: pageSize,
      offset,
      ...(query && isDocumentSearch
        ? { document: onlyDocumentChars(query) }
        : {}),
      ...(query && !isDocumentSearch ? { nameOrTradeName: query.trim() } : {}),
    };

    listResellerCustomers(params)
      .then((response) => {
        if (id !== requestId.current) return;
        setContracts(response.data.results ?? []);
        setTotal(response.data.total ?? 0);
      })
      .catch(() => {
        if (id !== requestId.current) return;
        toast.error('Falha ao carregar clientes');
        setContracts([]);
        setTotal(0);
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
  }, [page, pageSize, debouncedQuery]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const reload = useCallback(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const loadReport = useCallback(async () => {
    try {
      setLoadingReport(true);
      const response = await getResellerReport();
      setReportData(response.data);
    } catch (error) {
      console.error('Error loading reseller report', error);
      toast.error('Falha ao carregar relatório');
    } finally {
      setLoadingReport(false);
    }
  }, []);

  useEffect(() => {
    if (!canRead) return;
    loadReport();
  }, [loadReport, canRead]);

  const customers = useMemo(() => {
    const map = new Map<number, ResellerCustomer & { contractCount: number }>();
    for (const contract of contracts) {
      const c = contract.customer;
      if (!c) continue;
      const existing = map.get(c.id);
      if (existing) {
        existing.contractCount++;
      } else {
        map.set(c.id, { ...c, contractCount: 1 });
      }
    }
    return Array.from(map.values());
  }, [contracts]);

  const data = useMemo<ResellerContextData>(
    () => ({
      customers,
      loading,
      total,
      page,
      setPage,
      pageSize,
      setPageSize,
      searchText,
      setSearchText,
      createModalOpen,
      setCreateModalOpen,
      reload,
      reportData,
      loadingReport,
    }),
    [
      customers,
      loading,
      total,
      page,
      pageSize,
      searchText,
      setSearchText,
      createModalOpen,
      reload,
      reportData,
      loadingReport,
    ],
  );

  if (!canRead) {
    return (
      <Stack
        h="100vh"
        align="center"
        justify="center"
        gap="1.5rem"
        textAlign="center"
        p="2rem"
      >
        <Heading size="lg" color="gray.600">
          Acesso Restrito
        </Heading>
        <Text color="gray.500" maxW="400px">
          Você não possui permissão para acessar o módulo de revendedores.
          Solicite ao seu supervisor para acessar o módulo.
        </Text>
        <Box>
          <Button as={RouterLink} to="/" colorScheme="yooga">
            Voltar para a página inicial
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Context.Provider value={data}>
      <ResellerHome />
    </Context.Provider>
  );
};

export default ResellerContext;
